import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, ExternalLink, FileText, MessageCircle, Percent, Wallet } from 'lucide-react';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import { getCreditoWhatsAppHref } from '../../utils/creditoWhatsApp.js';
import publicCta from '../../content/publicCta.json';
import BrandLoading from '../../components/ui/BrandLoading.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import PublicPageHero from '../../components/public/PublicPageHero.jsx';
import { usePageSeo } from '../../hooks/usePageSeo.js';
import { stripHtmlToText } from '../../utils/seoMeta.js';
import { useVisibleLoading } from '../../hooks/useVisibleLoading.js';

const bodyProse =
  'max-w-none space-y-4 text-base leading-relaxed text-slate-600 [&_h1]:font-display [&_h2]:font-display [&_li]:ml-4 [&_p]:text-slate-600 [&_a]:text-accent-lime [&_a]:underline hover:[&_a]:text-[#0d2b7f]';

export default function CreditosDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const showLoading = useVisibleLoading(loading);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setNotFound(false);
      setError(null);
      try {
        const { data: res } = await api.get(`/v1/public/creditos/${id}`);
        if (cancelled) return;
        setData(res?.data ?? null);
      } catch (e) {
        if (cancelled) return;
        if (e.status === 404) setNotFound(true);
        else setError(e.message || 'No se pudo cargar el crédito');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (id) load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const html = (data?.contenido && String(data.contenido).trim()) || '';
  const whatsappHref = data ? getCreditoWhatsAppHref(data) : null;

  const heroTitle = showLoading
    ? 'Cargando…'
    : data?.titulo || (notFound ? 'Crédito no encontrado' : 'Línea de crédito');

  usePageSeo(
    showLoading || notFound
      ? { title: notFound ? 'Crédito no encontrado' : 'Línea de crédito' }
      : data
        ? {
            title: data.titulo,
            description: data.resumen || stripHtmlToText(data.contenido),
            image: data.imagen ? assetUrl(data.imagen) : undefined,
            type: 'article',
          }
        : null
  );

  const heroDescription = showLoading
    ? ''
    : data?.resumen ||
      (notFound
        ? 'Esta línea de crédito no está disponible o ya no se publica.'
        : error || '');

  return (
    <div className="pb-20">
      <PublicPageHero
        backTo="/creditos"
        backLabel="Volver a créditos"
        eyebrow={data?.categoria || 'Créditos'}
        title={heroTitle}
        description={heroDescription}
      />

      <section className="border-b border-slate-200 bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
      {showLoading && (
        <BrandLoading variant="inline" tone="light" label="Cargando crédito…" className="min-h-[32rem]" />
      )}

      {!showLoading && error && !data && <p className="text-slate-600">{error}</p>}

      {!showLoading && data && (
        <article className="space-y-8">
          <PremiumCard variant="light" className="overflow-hidden !p-6 sm:!p-8">
            <div className="grid gap-8 lg:grid-cols-12 lg:gap-10 lg:items-start">
              <div className="min-w-0 lg:col-span-5">
                <div className="relative aspect-[4/5] w-full max-w-md overflow-hidden rounded-xl bg-slate-900 ring-1 ring-slate-200 lg:max-w-none">
                  {data.imagen ? (
                    <img
                      src={assetUrl(data.imagen)}
                      alt=""
                      className="h-full w-full object-cover"
                      decoding="async"
                    />
                  ) : (
                    <div className="flex h-full min-h-[14rem] items-center justify-center px-6 text-sm font-medium uppercase tracking-wider text-slate-500">
                      Sin imagen
                    </div>
                  )}
                  {data.categoria ? (
                    <span className="absolute left-3 top-3 rounded-full bg-brand-navy px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white ring-1 ring-[#1acb12]/45">
                      {data.categoria}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="min-w-0 space-y-6 lg:col-span-7">
                <ul className="flex flex-wrap gap-3 text-sm text-slate-700">
                  {data.tasa_interes ? (
                    <li className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <Percent className="h-4 w-4 shrink-0 text-accent-lime" aria-hidden />
                      <span>
                        <span className="font-semibold text-slate-600">Interés: </span>
                        {data.tasa_interes}
                      </span>
                    </li>
                  ) : null}
                  {data.plazo ? (
                    <li className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <Clock className="h-4 w-4 shrink-0 text-accent-lime" aria-hidden />
                      {data.plazo}
                    </li>
                  ) : null}
                  {data.monto_maximo ? (
                    <li className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <Wallet className="h-4 w-4 shrink-0 text-accent-lime" aria-hidden />
                      {data.monto_maximo}
                    </li>
                  ) : null}
                </ul>

                {html ? (
                  <div className={bodyProse} dangerouslySetInnerHTML={{ __html: html }} />
                ) : (
                  <p className="text-slate-500">No hay detalle adicional publicado para esta línea.</p>
                )}

                {data.documento || whatsappHref ? (
                  <div className="flex flex-wrap gap-3 border-t border-slate-200 pt-6">
                    {data.documento ? (
                      <a
                        href={assetUrl(data.documento)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-[#1acb12]/40 bg-brand-green px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-green-dark"
                      >
                        <FileText className="h-4 w-4" aria-hidden />
                        Ver reglamento
                        <ExternalLink className="h-4 w-4" aria-hidden />
                      </a>
                    ) : null}
                    {whatsappHref ? (
                      <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-[#25D366]/50 bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#20bd5a]"
                      >
                        <MessageCircle className="h-4 w-4" aria-hidden />
                        {publicCta.creditosWhatsAppLabel}
                        <ExternalLink className="h-4 w-4" aria-hidden />
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </PremiumCard>

          <p className="text-center text-sm text-slate-500">
            Las condiciones definitivas se confirman al momento de la solicitud.{' '}
            {whatsappHref ? (
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[#25D366] hover:text-[#128C7E]"
              >
                Escríbenos por WhatsApp
              </a>
            ) : (
              <Link to="/contacto" className="font-semibold text-accent-lime hover:text-[#0d2b7f]">
                Contáctanos
              </Link>
            )}{' '}
            para más información sobre esta línea.
          </p>
        </article>
      )}
        </div>
      </section>
    </div>
  );
}
